"use client";

import type { ReactNode } from "react";
import { useState } from "react";
import { Button, Box, Text, Stack } from "@chakra-ui/react";
import { FcGoogle } from "react-icons/fc";
import { signIn } from "next-auth/react";

type GoogleSignInButtonProps = {
  children?: ReactNode;
  // Where NextAuth should send the browser after Google auth completes.
  redirectTo?: string;
  // Runs right before the Google redirect. /SignUp uses this to POST the
  // chosen role/age to /api/signup/intent first, since that's the only way
  // to carry state across the OAuth round-trip (Google doesn't pass through
  // custom data).
  onBeforeSignIn?: () => Promise<void>;
  // /SignUp uses "Sign Up with Google" since this button creates an
  // account there; /SignIn keeps the default.
  label?: string;
};

export function GoogleSignInButton({
  children,
  redirectTo = "/Dashboard",
  onBeforeSignIn,
  label = "Sign in with Google",
}: GoogleSignInButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    try {
      setLoading(true);
      setError(null);

      if (onBeforeSignIn) {
        await onBeforeSignIn();
      }

      await signIn("google", {
        redirectTo,
      });
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <Stack gap={3} align="center">
      <Button
        size="md"
        rounded="lg"
        fontWeight="bold"
        variant="outline"
        colorPalette="white"
        border="1.2px solid #0681B1"
        loading={loading}
        onClick={handleClick}
        disabled={loading}
      >

          <Box color="currentColor" display="inline-flex" alignItems="center">
            <FcGoogle size={16}/>
          </Box>
          <Text fontSize="sm">
            {label}
          </Text>

      </Button>

      {children}

      {error ? (
        <Text fontSize="sm" color="red.500" textAlign="center">
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}
